import {ChangeDetectionStrategy, Component, computed, effect, input, output, signal} from "@angular/core";
import {MatMenuModule} from "@angular/material/menu";
import {MatIcon} from "@angular/material/icon";
import {IconButtonComponent} from "@common/ui/icon-button/icon-button.component";
import {FontAwesomeCode} from "@models";
import {MatDividerModule} from "@angular/material/divider";
import {SegmentedButtonComponent} from "@common/ui/segmented-button/segmented-button.component";
import {ButtonSegmentComponent} from "@common/ui/segmented-button/button-segment/button-segment.component";
import {MatTooltipModule} from "@angular/material/tooltip";
import {Translations} from "@translations";
import {TranslatePipe} from "@ngx-translate/core";

export type CalendarSortValue = "dateTime" | "name";
export type CalendarSortOrder = "asc" | "desc";

export type CalendarSortOption = {
	value: CalendarSortValue;
	label: string;
	icon: FontAwesomeCode;
};

@Component({
	selector: "sim-calendar-sorting",
	templateUrl: "./calendar-sorting.component.html",
	styleUrl: "./calendar-sorting.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatMenuModule,
		MatIcon,
		IconButtonComponent,
		MatDividerModule,
		SegmentedButtonComponent,
		ButtonSegmentComponent,
		MatTooltipModule,
		TranslatePipe,
	],
})
export class CalendarSortingComponent {
	// I/O
	readonly initSortOrder = input<CalendarSortOrder>("asc");
	readonly sortChanged = output<CalendarSortValue>();
	readonly sortOrderChanged = output<CalendarSortOrder>();

	// Constants
	protected readonly T = Translations;
	protected readonly selectedValue = signal<CalendarSortValue>("dateTime");
	protected readonly sortOrder = signal<CalendarSortOrder>("asc");
	protected readonly activeOrderSegment = computed<number>(() => (this.sortOrder() === "asc" ? 0 : 1));
	protected readonly ascTooltip = computed<string>(() =>
		this.selectedValue() === "dateTime" ? this.T.CALENDAR_SORT_EARLIEST_FIRST : this.T.CALENDAR_SORT_A_TO_Z,
	);
	protected readonly descTooltip = computed<string>(() =>
		this.selectedValue() === "dateTime" ? this.T.CALENDAR_SORT_LATEST_FIRST : this.T.CALENDAR_SORT_Z_TO_A,
	);
	protected readonly sortOptions: CalendarSortOption[] = [
		{
			value: "dateTime",
			label: this.T.CALENDAR_SORT_DATE_TIME,
			icon: {fontSet: "fad", fontIcon: "fa-calendar-clock"},
		},
		{
			value: "name",
			label: this.T.CALENDAR_SORT_NAME_TIME,
			icon: {fontSet: "fad", fontIcon: "fa-arrow-up-big-small"},
		},
	];

	readonly #initSortOrderEffect = effect(() => {
		this.sortOrder.set(this.initSortOrder());
	});

	protected selectOption(value: CalendarSortValue): void {
		this.selectedValue.set(value);
		this.sortChanged.emit(value);
	}

	protected selectOrder(order: CalendarSortOrder): void {
		this.sortOrder.set(order);
		this.sortOrderChanged.emit(order);
	}
}
